import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table" 
import { MoveRightIcon, PlusCircleIcon } from "lucide-react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  MixerHorizontalIcon
} from "@radix-ui/react-icons"
import {Route as PendingAcceptanceRoute} from "@/routes/(pharma)/_layout/inventory/pending-acceptance-inventory/index"
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import { cn } from "@/lib/utils";
import PaginationButton from "@/components/ui/pagination-button";
import {
  TableCell as TableCellOrig,
  TableHead as TableHeadOrig,
} from "@/components/ui/table";
import { useState } from "react";
import { pharmacyClient } from "@/connect";
import { useRouter } from "@tanstack/react-router";

const Component = () => {
  const searchParams = PendingAcceptanceRoute.useSearch();
  const data = PendingAcceptanceRoute.useLoaderData();
  const navigate = useNavigate();

  const columnOptions = [
    { key: "trade_name", label: "Trade Name" },
    { key: "unit", label: "Unit" },
    { key: "unit_type", label: "Unit Type" },
    { key: "previous_price", label: "Previous Price" },
    { key: "price", label: "Updated Price" },
  ];
  const [visibleColumns, setVisibleColumns] = useState(() =>
    Object.fromEntries(columnOptions.map(col => [col.key, true]))
  );

  const handleToggleColumn = (key: string) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const TableCell = ({ className = "", ...props }) => (
    <TableCellOrig className={cn(tableCellClass, "text-sm font-medium py-0", className)} {...props} />
  );

  const TableHead = ({ className = "", ...props }) => (
    <TableHeadOrig className={cn(tableHeadClass, className)} {...props} />
  );

  const router = useRouter()

  return (
    <>
      <div className="overflow-y-scroll h-screen">
        <div className="flex items-center justify-between">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink onClick={() => {
                  navigate({
                    to: '/inventory',
                  })
                }} style={{ cursor: 'pointer' }}>Inventory Overview</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Pending Price Acceptance</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <hr className="my-4" />
        <div className="flex items-center justify-between">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="hidden h-8 lg:flex"
              >
                <MixerHorizontalIcon className="mr-2 h-4 w-4" />
                View
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[180px]">
              <DropdownMenuLabel>Columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columnOptions.map(col => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={visibleColumns[col.key]}
                  onCheckedChange={() => handleToggleColumn(col.key)}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            size={"sm"}
          >Accept All</Button>
        </div>
        <div className="border rounded-lg bg-white overflow-hidden mt-4">
          <Table className="text-sm">
            <TableHeader>
              <TableRow>
                {visibleColumns.trade_name && <TableHead>Trade Name</TableHead>}
                {visibleColumns.unit && <TableHead>Unit</TableHead>}
                {visibleColumns.unit_type && <TableHead>Unit Type</TableHead>}
                {visibleColumns.previous_price && <TableHead>Previous Price</TableHead>}
                {visibleColumns.price && <TableHead>Updated Price</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.pricingPendingAcceptances.map((item, index) => (
                <TableRow key={index}>
                  {visibleColumns.trade_name && (
                    <TableCell className={index === data.pricingPendingAcceptances.length - 1 ? tableCellLastRowClass : ""}>{item.tradeName}</TableCell>
                  )}
                  {visibleColumns.unit && (
                    <TableCell className={index === data.pricingPendingAcceptances.length - 1 ? tableCellLastRowClass : ""}>{item.unit}</TableCell>
                  )}
                  {visibleColumns.unit_type && (
                    <TableCell className={index === data.pricingPendingAcceptances.length - 1 ? tableCellLastRowClass : ""}>{item.unitType}</TableCell>
                  )}
                  {visibleColumns.previous_price && (
                    <TableCell className={index === data.pricingPendingAcceptances.length - 1 ? tableCellLastRowClass : ""}>{convertToCurrencyRounded(item.oldPrice)}</TableCell>
                  )}
                  {visibleColumns.price && (
                    <TableCell className={index === data.pricingPendingAcceptances.length - 1 ? tableCellLastRowClass : ""}>{convertToCurrencyRounded(item.oldPrice)} to {convertToCurrencyRounded(item.price)}</TableCell>
                  )}
                  <TableCell className={index === data.pricingPendingAcceptances.length - 1 ? tableCellLastRowClass : ""}><Button size="sm" variant="outline" onClick={async () => {
                    await pharmacyClient.acceptPricingOne({
                      priceHistoryId: item.priceHistoryId
                    })
                    router.invalidate()
                  }}>Accept Price</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
            <p>{data.totalCount} <span className="text-gray-500 font-light">count</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: 1
                    }
                  })
                }}
                disabled={searchParams.page === 1}
              >
                <DoubleArrowLeftIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: searchParams.page - 1
                    }
                  })
                }}
                disabled={searchParams.page === 1}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </PaginationButton>
              {(() => {
                const totalPages = data.totalCount > 0 ? Math.ceil(data.totalCount / searchParams.pageSize) : 1;
                const getPageNumbers = () => {
                  const pages = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    if (searchParams.page > 3) pages.push('...');
                    for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
                      pages.push(i);
                    }
                    if (searchParams.page < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages;
                };
                return getPageNumbers().map((page, idx) => (
                  page === '...'
                    ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                    : <PaginationButton
                        key={page}
                        active={searchParams.page === page}
                        onClick={() => {
                          navigate({
                            search: {
                              ...searchParams,
                              page: typeof page === 'number' ? page : searchParams.page
                            }
                          });
                        }}
                      >
                        <span className="text-sm">{page}</span>
                      </PaginationButton>
                ));
              })()}
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: searchParams.page + 1
                    }
                  })
                }}
                disabled={data.totalCount === 0 || searchParams.page === (data.totalCount > 0 ? Math.ceil(data.totalCount / searchParams.pageSize) : 1)}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      page: data.totalCount > 0 ? Math.ceil(data.totalCount / searchParams.pageSize) : 1
                    }
                  })
                }}
                disabled={data.totalCount === 0 || searchParams.page === (data.totalCount > 0 ? Math.ceil(data.totalCount / searchParams.pageSize) : 1)}
              >
                <DoubleArrowRightIcon className="h-4 w-4" />
              </PaginationButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Component;
